import {
  AccountBalanceDto,
  OfferOrderCardPaymentDetailsDto,
  OfferOrderCardPaymentDto,
  OfferOrderRefundDto,
  OfferOrderDetailsDto,
  OfferOrderDto,
  OfferOrderSecurityDeliveryOptionDto,
  OfferOrderAccountDto,
} from 'src/dtos';
import {
  AccountStatus,
  OfferOrder,
  OfferOrderCardPayment,
  OfferOrderCardPaymentDetails,
  OfferOrderCardPaymentIntentStatus,
  OfferOrderRefund,
  OfferOrderRefundReason,
  OfferOrderRefundStatus,
  OfferOrderDetails,
  OfferOrderPaymentType,
  OfferOrderStatus,
  OfferOrderPaymentStatus,
  OfferOrderRetirementAccountFundingType,
  OfferOrderAccount,
  AccountType,
  JointAccountType,
  RetirementAccountType,
  EntityAccountType,
} from 'src/models';

import { findCashAvailable } from './accountBalances.mappers';

export const replaceSecurityDeliveryOptionTooltipIfFound = (
  securityDeliveryOption?: OfferOrderSecurityDeliveryOptionDto,
): string => {
  if (!securityDeliveryOption) return '';
  if (!securityDeliveryOption.tooltip) return securityDeliveryOption.label;

  let result = securityDeliveryOption.label;
  result = result.replace(/<\/?tooltip>/g, '');

  return result;
};

export const mapOfferOrderRefundDtoListToModel = (list: OfferOrderRefundDto[]): OfferOrderRefund[] =>
  list.map(aDto => ({
    id: aDto.id,
    amount: aDto.amount,
    mechanism: aDto.mechanism,
    externalId: aDto.externalId,
    reason: new OfferOrderRefundReason(aDto.reason),
    status: new OfferOrderRefundStatus(aDto.status),
    description: aDto.description,
    failureReason: aDto.failureReason,
    bankName: aDto.bankName,
    adminNote: aDto.adminNote,
    createdAt: aDto.createdAt,
    updatedAt: aDto.updatedAt,
  }));

export const mapOfferOrderCardPaymentDtoToModel = (dto: OfferOrderCardPaymentDto): OfferOrderCardPayment => ({
  ...dto,
  intentStatus: new OfferOrderCardPaymentIntentStatus(dto.intentStatus),
});

export const mapOfferOrderCardPaymentListDtoToModel = (dtoList: OfferOrderCardPaymentDto[]): OfferOrderCardPayment[] =>
  dtoList.map(mapOfferOrderCardPaymentDtoToModel);

export const mapOfferOrderCardPaymentDetailsDtoToModel = (
  dto: OfferOrderCardPaymentDetailsDto,
): OfferOrderCardPaymentDetails => mapOfferOrderCardPaymentDtoToModel(dto);

const findOfferOrderAccountTypeLabel = ({
  type,
  retirementAccountType,
  entityAccountType,
}: {
  type: AccountType;
  jointAccountType?: JointAccountType;
  retirementAccountType?: RetirementAccountType;
  entityAccountType?: EntityAccountType;
}): string => {
  if (type.isRetirement) {
    return retirementAccountType?.label ?? type.label;
  }

  if (type.isEntity) {
    return entityAccountType?.label ?? type.label;
  }

  return type.label;
};

export const mapOfferOrderAccountDtoToModel = (dto: OfferOrderAccountDto): OfferOrderAccount => {
  const type = dto.type ? new AccountType(dto.type) : undefined;
  const jointAccountType = dto.jointAccountType ? new JointAccountType(dto.jointAccountType) : undefined;
  const retirementAccountType = dto.retirementAccountType
    ? new RetirementAccountType(dto.retirementAccountType)
    : undefined;
  const entityAccountType = dto.entityAccountType ? new EntityAccountType(dto.entityAccountType) : undefined;

  return {
    id: dto.id,
    accountId: dto.accountId,
    accountNumber: dto.accountNumber,
    status: dto.status ? new AccountStatus(dto.status) : undefined,
    type,
    jointAccountType,
    retirementAccountType,
    entityAccountType,
    typeLabel: type
      ? findOfferOrderAccountTypeLabel({ type, jointAccountType, retirementAccountType, entityAccountType })
      : undefined,
    primaryAccountHolder: dto.primaryAccountHolder
      ? {
          id: dto.primaryAccountHolder.id,
          firstName: dto.primaryAccountHolder.firstName,
          middleName: dto.primaryAccountHolder.middleName,
          lastName: dto.primaryAccountHolder.lastName,
          suffix: dto.primaryAccountHolder.suffix,
          fullName: [
            dto.primaryAccountHolder.firstName,
            dto.primaryAccountHolder.middleName,
            dto.primaryAccountHolder.lastName,
            dto.primaryAccountHolder.suffix,
          ]
            .filter(Boolean)
            .join(' '),
          email: dto.primaryAccountHolder.email,
        }
      : undefined,
  };
};

export const mapOfferOrderDtoToModel = (dto: OfferOrderDto, balance?: AccountBalanceDto): OfferOrder => {
  const { cardPayment, ...baseDto } = dto;

  const result: OfferOrder = {
    ...baseDto,
    paymentType: new OfferOrderPaymentType(dto.paymentType),
    paymentStatus: new OfferOrderPaymentStatus(dto),
    status: new OfferOrderStatus(dto.status),
    cashBalance: balance?.cashBalance,
    cashAvailable: balance ? findCashAvailable(balance) : undefined,
    refunds: mapOfferOrderRefundDtoListToModel(dto.refunds),
    retirementAccountFundingType: dto.retirementAccountFundingType
      ? new OfferOrderRetirementAccountFundingType(dto.retirementAccountFundingType)
      : undefined,
    account: mapOfferOrderAccountDtoToModel(dto.account),
  };

  if (cardPayment) {
    result.cardPayment = mapOfferOrderCardPaymentDtoToModel(cardPayment);
  }

  return result;
};

export const mapOfferOrderDetailsDtoToModel = (dto: OfferOrderDetailsDto): OfferOrderDetails => {
  const result: OfferOrderDetails = {
    ...mapOfferOrderDtoToModel(dto),
    paymentInstructions: dto.paymentInstructions,
    analytics: dto.analytics,
    subscriptionAgreementFilename: dto.subscriptionAgreementFilename,
    subscriptionAgreementFilepath: dto.subscriptionAgreementFilepath,
    lastFailedSendConfirmationEmailError: dto.lastFailedSendConfirmationEmailError,
    lastFailedSendConfirmationEmailAttemptAt: dto.lastFailedSendConfirmationEmailAttemptAt,
    lastFailedSendOversubscriptionEmailError: dto.lastFailedSendOversubscriptionEmailError,
    lastFailedSendOversubscriptionEmailAttemptAt: dto.lastFailedSendOversubscriptionEmailAttemptAt,
  };

  return result;
};
